/**
 * The symbol in front of a price — cities plan C2.3 (D4).
 *
 * `€` was hard-coded in `rowMeta.ts`, `deskBrowse.ts` and the lists. A region
 * has a `currency` (`regions.currency`, 'EUR' / 'USD'), a row belongs to a
 * region through its town, and the region decides: **no symbol is guessed from
 * a price string**, and none from the zone either. Dates stay `en-GB` order
 * and the clock stays 24-hour in every region (Doug, 8 Oct); this is the one
 * thing about money that follows the region.
 *
 * Two questions, kept apart as `zone.ts` keeps its two:
 *
 *   - `currencyOfCity(row.city)` — the currency of the region a row's town
 *     belongs to. What a row's price is printed in. A town no region lists, or
 *     a row with no city, is a Berlin row (survey trap T2) and reads EUR.
 *   - `hereCurrency()` — the current region's currency. What a *new* price
 *     defaults to: an item added to a list while in Boston is in dollars, an
 *     item already priced keeps the currency it was priced in.
 *
 * Ambient, like `zone.ts`, because `rowMeta.ts` and `deskBrowse.ts` cannot
 * import React or Supabase and are called from everywhere. `currentRegion.ts`
 * installs the real resolver (bundled at load, live at the app root); until it
 * has, both answer `FALLBACK_CURRENCY`, which is what the Worker's suite reads.
 *
 * Import-free apart from `reach.ts` (itself import-free), by its full name.
 */
import { regionOf, type ReachTown } from './reach.ts'

/** Berlin's currency, and so every unplaceable row's (trap T2). */
export const FALLBACK_CURRENCY = 'EUR'

/** 'EUR' → '€', 'USD' → '$', anything else → the code itself. */
export function priceSymbol(currency: string): string {
  // A `Map`, not an object literal: `SYMBOLS['constructor']` would answer out
  // of `Object.prototype` (CLAUDE.md's alias-map bug), and the currency of a
  // list item is a free-text column.
  return SYMBOLS.get(currency.trim().toUpperCase()) ?? currency
}
const SYMBOLS = new Map<string, string>([['EUR', '€'], ['USD', '$']])

export interface Currencies {
  /** The current region's currency. */
  here: () => string
  /** A row's currency, from its town. Never throws. */
  of: (city: string | null | undefined) => string
}

let currencies: Currencies = { here: () => FALLBACK_CURRENCY, of: () => FALLBACK_CURRENCY }

/** Installed by `currentRegion.ts`. Idempotent. */
export function setCurrencies(next: Currencies): void {
  currencies = next
}

/** The current region's currency — what a new price defaults to. */
export function hereCurrency(): string {
  return currencies.here()
}

/** The currency of the region a row's town belongs to, or Berlin's (T2). */
export function currencyOfCity(city: string | null | undefined): string {
  return currencies.of(city)
}

/** The shape this reads from `regions.ts`'s `RegionsData`, declared rather
 *  than imported so this file stays import-free. */
export interface CurrencySource {
  regions: ReadonlyMap<string, { currency: string }>
  towns: readonly ReachTown[]
}

/** A `Currencies` over `data`. `here` is a function, not a value, because the
 *  current region moves under it (C2.2) and the caller knows which it is. The
 *  town lookup is memoised per city, as `zonesOf`'s is. */
export function currenciesOf(data: CurrencySource, hereId: () => string): Currencies {
  const memo = new Map<string, string>()
  return {
    here: () => data.regions.get(hereId())?.currency ?? FALLBACK_CURRENCY,
    of: city => {
      const key = city ?? ''
      let c = memo.get(key)
      if (c === undefined) {
        const id = regionOf(city ?? null, data.towns)
        c = (id && data.regions.get(id)?.currency) || FALLBACK_CURRENCY
        memo.set(key, c)
      }
      return c
    },
  }
}
